import { useState } from 'react'
import { cva } from 'class-variance-authority'
import { useMatch, useNavigate } from 'react-router-dom'

import { cn } from '@/utils/cn'
import type { NavItem } from '@/config/navigation'

import NavigationItem, { navItemIconCls } from './NavigationItem'

const expandableBtnCls = cva(
  'group/navItem focus-visible:ring-2 focus-visible:ring-elements-dark-highlight flex w-full items-center rounded-md py-2 pr-1 pl-2 text-sm font-medium outline-none transition-colors',
  {
    variants: {
      active: {
        true: 'bg-elements-dark-dimmed text-elements-dark-contrastText hover:bg-elements-dark-dimmed',
        false:
          'text-elements-dark-contrastText/70 hover:bg-elements-dark-dimmed hover:text-elements-dark-contrastText',
      },
    },
    defaultVariants: { active: false },
  },
)

interface PropsI extends NavItem {
  visibleChildIds?: string[]
}

const NavigationExpandable = ({
  id,
  label,
  icon: Icon,
  url,
  children = [],
  expandable = true,
  defaultExpanded = false,
  visibleChildIds,
}: PropsI) => {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(expandable ? defaultExpanded : true)
  const isActive = !!useMatch({ path: url, end: !expandable })

  const visibleChildren = visibleChildIds
    ? children.filter((c) => visibleChildIds.includes(c.id))
    : children.filter((c) => !c.hidden)

  return (
    <div className='space-y-1'>
      <button
        type='button'
        onClick={() => {
          if (!expandable) {
            navigate(url)
          } else {
            setExpanded((e) => !e)
          }
        }}
        className={expandableBtnCls({ active: isActive })}
        aria-controls={`sub-menu-${id}`}
        aria-expanded={expanded}
      >
        <Icon className={cn(navItemIconCls({ active: isActive }))} />
        <span className='flex-1 text-left'>{label}</span>
        {expandable && (
          <svg
            className={cn(
              'group-hover/navItem:text-elements-dark-highlight ml-auto h-5 w-5 transform transition-all duration-150',
              {
                'text-elements-dark-contrastText': isActive,
                'rotate-90': expanded,
                'text-elements-dark-contrastText/40': !isActive,
              },
            )}
            viewBox='0 0 20 20'
            aria-hidden='true'
          >
            <path d='M6 6L14 10L6 14V6Z' fill='currentColor' />
          </svg>
        )}
      </button>

      <div
        id={`sub-menu-${id}`}
        className={cn('space-y-1', { hidden: !expanded && expandable })}
      >
        {visibleChildren.map((item) => (
          <NavigationItem
            key={item.id}
            className='w-full pr-2 pl-6'
            {...item}
          />
        ))}
      </div>
    </div>
  )
}

export default NavigationExpandable
